import ManagerLayout from "../../layouts/ManagerLayout";
import useAuthStore from "../../store/authStore";
import AdminLayout from "../../layouts/AdminLayout";
import { useNavigate, useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { useState, useMemo, useEffect, useRef } from "react";
import {
  getProjects,
  getProjectDisciplines,
  getDisciplineStages,
  getManagerActivities,
  createManagerActivity,
  updateManagerActivity,
  deleteManagerActivity,
} from "../../api/projectService";
import { getAttendance } from "../../api/attendanceService";
import { getTeamTimesheet } from "../../api/employeeService";
import { getUserById } from "../../api/accountsService";

// ── Helpers ────────────────────────────────────────────────────────────────────
function toHHMM(decimalHrs) {
  if (decimalHrs === "" || decimalHrs === null || decimalHrs === undefined) return "00:00";
  const num = parseFloat(decimalHrs);
  if (isNaN(num) || num < 0) return "00:00";
  const h = Math.floor(num);
  const m = Math.round((num - h) * 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function hhmmToDecimal(str) {
  if (!str || !str.includes(":")) { const n = parseFloat(str); return isNaN(n) ? 0 : n; }
  const [h, m] = str.split(":").map(Number);
  return (isNaN(h) ? 0 : h) + (isNaN(m) ? 0 : m) / 60;
}

// Fallback dropdown options removed, fetched dynamically from backend only


// ── Example data ───────────────────────────────────────────────────────────────
let _nextId = 1000;
const makeBlankRow = () => ({
  id: ++_nextId, date: "", project: "", discipline: "", stageName: "",
  description: "", duration: "", isNew: true,
});

const getMonday = (d = new Date()) => {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const mon = new Date(d.setDate(diff));
  mon.setHours(0, 0, 0, 0);
  return mon;
};

const getTodayKey = () => {
  const day = new Date().getDay();
  const keys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  return keys[day];
};

const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};

const fmtDate = (d) => String(d.getDate()).padStart(2, "0");
const fmtMonthName = (d) => d.toLocaleDateString("en-US", { month: "short" });
const fmtISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// ── Discipline badge colors ───────────────────────────────────────────────────
const DISCIPLINE_COLORS = {
  Meeting: { bg: "bg-purple-500/20", text: "text-purple-300", border: "border-purple-400/30" },
  Review: { bg: "bg-emerald-500/20", text: "text-emerald-300", border: "border-emerald-400/30" },
  Design: { bg: "bg-indigo-500/20", text: "text-indigo-300", border: "border-indigo-400/30" },
  Admin: { bg: "bg-amber-500/20", text: "text-amber-300", border: "border-amber-400/30" },
  Site: { bg: "bg-orange-500/20", text: "text-orange-300", border: "border-orange-400/30" },
  Client: { bg: "bg-sky-500/20", text: "text-sky-300", border: "border-sky-400/30" },
  default: { bg: "bg-white/10", text: "text-white/60", border: "border-white/20" },
};

// function StageBadge({ stage }) {
//   const c = STAGE_COLORS[stage] || STAGE_COLORS.default;
//   return <span className={`inline-block px-2.5 py-1 rounded-lg text-xs font-semibold ${c.bg} ${c.text}`}>{stage}</span>;
// }

// ── Reusable inline dropdown ───────────────────────────────────────────────────
function InlineSelect({ value, onChange, options, placeholder, colorMap, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const updateCoords = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  useEffect(() => {
    if (isOpen) {
      updateCoords();
      window.addEventListener("scroll", updateCoords, true);
      window.addEventListener("resize", updateCoords);
    }
    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm(value || "");
    }
  }, [value, isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter(option =>
    (option || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const c = colorMap ? (colorMap[value] || colorMap.default) : null;

  return (
    <div ref={containerRef} className="relative w-full text-left">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : (value || "")}
          placeholder={value || placeholder}
          disabled={disabled}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            setSearchTerm("");
            setIsOpen(true);
          }}
          className={`w-full text-xs font-semibold rounded-lg pl-2 pr-6 py-1.5 border focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 transition-all cursor-pointer placeholder-white/40
            ${disabled ? "opacity-75 cursor-not-allowed" : ""}
            ${value
              ? c
                ? `${c.bg} ${c.text} ${c.border} border`
                : "bg-white/10 border-white/20 text-white"
              : "bg-white/10 border-white/20 text-white/45"
            }`}
        />
        {!disabled && (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] text-white/40 pointer-events-none">
            ▼
          </span>
        )}
      </div>

      {isOpen && !disabled && createPortal(
        <ul
          ref={dropdownRef}
          style={{
            position: "absolute",
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            minWidth: `max(250px, ${coords.width}px)`,
          }}
          className="z-[9999] mt-1 max-h-40 overflow-y-auto bg-slate-900 border border-white/15 rounded-lg shadow-2xl py-1 text-xs select-none text-white no-scrollbar"
        >
          {filteredOptions.length === 0 ? (
            <li className="px-3 py-2 text-white/45 italic text-center">No matches</li>
          ) : (
            filteredOptions.map((o) => (
              <li
                key={o}
                onClick={() => {
                  onChange(o);
                  setSearchTerm(o);
                  setIsOpen(false);
                }}
                className={`px-3 py-1.5 hover:bg-indigo-600 text-white cursor-pointer transition-colors whitespace-nowrap ${o === value ? "bg-indigo-500/35 font-bold" : ""}`}
              >
                {o}
              </li>
            ))
          )}
        </ul>,
        document.body
      )}
    </div>
  );
}

// ── Smart HH:MM input ──────────────────────────────────────────────────────────
function TimeInput({ value, onChange, disabled }) {
  const [raw, setRaw] = useState(value ? toHHMM(value) : "");
  useMemo(() => { setRaw(value ? toHHMM(value) : ""); }, [value]);

  const handleChange = (e) => {
    let v = e.target.value.replace(/[^0-9:]/g, "");
    if (v.length === 2 && !v.includes(":")) v = v + ":";
    if (v.length > 5) v = v.slice(0, 5);
    setRaw(v);
  };
  const handleBlur = () => {
    const n = raw.trim();
    if (!n) { onChange(""); return; }
    if (n.includes(":")) {
      const [h, m] = n.split(":").map((x) => parseInt(x, 10) || 0);
      const d = h + m / 60;
      const rounded = Math.round(d * 100) / 100;
      setRaw(toHHMM(rounded));
      onChange(String(rounded));
    } else {
      const f = parseFloat(n);
      if (!isNaN(f)) {
        const rounded = Math.round(f * 100) / 100;
        setRaw(toHHMM(rounded));
        onChange(String(rounded));
      }
      else { setRaw(""); onChange(""); }
    }
  };
  return (
    <input type="text" inputMode="numeric" value={raw} onChange={handleChange} onBlur={handleBlur}
      placeholder="00:00"
      disabled={disabled}
      className={`w-16 text-center bg-white/10 border border-white/25 rounded-lg px-1 py-1.5 text-white text-xs font-semibold font-mono focus:outline-none focus:border-indigo-400 focus:bg-white/20 placeholder-white/25 transition-all
        ${disabled ? "opacity-75 cursor-not-allowed" : ""}`}
    />
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function ManagerActivity() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get("employee_id") || "";
  const loggedUserId = useAuthStore((state) => state.employeeId);
  const isOwnTimesheet = !employeeId || employeeId === loggedUserId;
  const userRole = useAuthStore((state) => state.role) || 'MANAGER';
  const Layout = userRole === "ADMIN" ? AdminLayout : ManagerLayout;
  const canEditExisting = isOwnTimesheet || (userRole === "MANAGER");

  const [resolvedManagerId, setResolvedManagerId] = useState(searchParams.get("manager_id") || "");
  const [reportingManagers, setReportingManagers] = useState([]);
  const [monday] = useState(getMonday());

  useEffect(() => {
    const urlManagerId = searchParams.get("manager_id");
    if (urlManagerId) {
      setResolvedManagerId(urlManagerId);
    }
  }, [searchParams]);

  useEffect(() => {
    if (userRole === "ADMIN" && employeeId && !resolvedManagerId) {
      getUserById(employeeId)
        .then((res) => {
          if (res.data && res.data.user) {
            if (res.data.user.manager_id) {
              setResolvedManagerId(res.data.user.manager_id);
            } else if (res.data.user.role === "MANAGER") {
              setResolvedManagerId(res.data.user.employee_id);
            }
          }
        })
        .catch((err) => {
          console.error("Failed to load user manager info:", err);
        });
    }
  }, [userRole, employeeId, resolvedManagerId]);
  const [selectedKey, setSelectedKey] = useState(getTodayKey());
  const [weekData, setWeekData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [dbProjects, setDbProjects] = useState([]);
  const [projectDisciplines, setProjectDisciplines] = useState({});
  const [disciplineStages, setDisciplineStages] = useState({});

  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  }, [monday]);

  const fetchDisciplinesForProject = async (projectId) => {
    if (!projectId || projectDisciplines[projectId]) return;
    try {
      const res = await getProjectDisciplines(projectId);
      setProjectDisciplines(prev => ({ ...prev, [projectId]: res.data || [] }));
    } catch (err) {
      console.error("Failed to fetch disciplines", err);
    }
  };

  const fetchStagesForDiscipline = async (projectId, disciplineId) => {
    const key = `${projectId}_${disciplineId}`;
    if (!projectId || !disciplineId || disciplineStages[key]) return;
    try {
      const res = await getDisciplineStages(projectId, disciplineId);
      setDisciplineStages(prev => ({ ...prev, [key]: res.data || [] }));
    } catch (err) {
      console.error("Failed to fetch stages", err);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch attendance for both months if the week straddles a month boundary
      const sundayDate = addDays(monday, 6);
      const monMonth = monday.getMonth() + 1;
      const sunMonth = sundayDate.getMonth() + 1;
      const monYear = monday.getFullYear();
      const sunYear = sundayDate.getFullYear();

      const attParams = { month: monMonth, year: monYear };
      if (employeeId && employeeId !== loggedUserId) {
        attParams.employee_id = employeeId;
      }
      const attFetches = [getAttendance(attParams)];

      if (sunMonth !== monMonth || sunYear !== monYear) {
        const sunAttParams = { month: sunMonth, year: sunYear };
        if (employeeId && employeeId !== loggedUserId) {
          sunAttParams.employee_id = employeeId;
        }
        attFetches.push(getAttendance(sunAttParams));
      }

      const actParams = { week_start: fmtISO(monday) };
      if (employeeId && employeeId !== loggedUserId) {
        actParams.employee_id = employeeId;
      }

      const [actRes, projRes, ...attResults] = await Promise.all([
        getManagerActivities(actParams),
        getProjects(),
        ...attFetches,
      ]);

      const fetchedActivities = actRes.data || [];
      const projectsList = projRes.data || [];
      setDbProjects(projectsList);

      // Merge attendance lists from all fetched months
      const attendanceList = attResults.flatMap(r => r.data?.attendances || []);

      // Build attendance map: key = YYYY-MM-DD, value = { hours, hasRecord, status, checkedIn }
      const attendanceMap = {};
      attendanceList.forEach((att) => {
        if (att.date) {
          attendanceMap[att.date] = {
            hours: att.working_hours != null ? parseFloat(att.working_hours) : 0.0,
            hasRecord: true,
            status: att.status,
            checkedIn: att.check_in != null,
            has_override: att.has_override,
          };
        }
      });

      const computedDisciplinesMap = { ...projectDisciplines };
      const computedStagesMap = { ...disciplineStages };

      // Parallel background prefetch for all disciplines + stages in one batch
      const prefetchTasks = fetchedActivities.map(async (a) => {
        const proj = projectsList.find(p =>
          p.project_name === a.project_name ||
          `${p.project_id} --- ${p.project_name}` === a.project_name ||
          `${p.project_id} ${p.project_name}` === a.project_name ||
          `[${p.project_id}] ${p.project_name}` === a.project_name
        );
        if (!proj) return;
        const pid = proj.project_id;
        let currentDisciplines = computedDisciplinesMap[pid];
        if (!currentDisciplines) {
          try {
            const disciplinesRes = await getProjectDisciplines(pid);
            currentDisciplines = disciplinesRes.data || [];
            computedDisciplinesMap[pid] = currentDisciplines;
          } catch (err) { console.error(err); return; }
        }
        if (a.discipline_name && currentDisciplines) {
          const disciplineObj = currentDisciplines.find(s => s.discipline_id.toUpperCase() === a.discipline_name.toUpperCase());
          if (disciplineObj) {
            const key = `${pid}_${disciplineObj.discipline_id}`;
            if (!computedStagesMap[key]) {
              try {
                const stagesRes = await getDisciplineStages(pid, disciplineObj.discipline_id);
                computedStagesMap[key] = stagesRes.data || [];
              } catch (err) { console.error(err); }
            }
          }
        }
      });
      await Promise.all(prefetchTasks).catch(() => { });
      setProjectDisciplines(computedDisciplinesMap);
      setDisciplineStages(computedStagesMap);

      const computedDays = weekDates.map((d, i) => {
        const dateStr = fmtISO(d);
        const dayActivities = fetchedActivities
          .filter((a) => a.activity_date === dateStr)
          .map((a) => ({
            id: a.id,
            date: `${fmtDate(d)}-${fmtMonthName(d)}`,
            project: a.project_name || "",
            discipline: a.discipline_name || "",
            stageName: a.stage_name || "",
            description: a.description || "",
            duration: a.duration_hours !== null && a.duration_hours !== undefined ? String(a.duration_hours) : "",
            isNew: false,
          }));

        const attEntry = attendanceMap[dateStr] || null;
        const isHoliday = attEntry ? attEntry.status === "HOLIDAY" : false;
        const isWeekOff = attEntry ? attEntry.status === "WEEK_OFF" : (d.getDay() === 0);
        const isOnLeave = attEntry ? attEntry.status === "ON_LEAVE" : false;
        const checkedIn = attEntry ? attEntry.checkedIn : false;

        const isHolidayOrWeekOff = isHoliday || isWeekOff;
        const hasOverride = attEntry ? attEntry.has_override === true : false;
        // Also unlock if the manager physically checked in on this holiday/week-off
        // (they may not have checked out yet, so HolidayWorkRequest may not exist yet)
        const isDisabled = isOnLeave || (isHolidayOrWeekOff && !hasOverride && !checkedIn);

        return {
          label: DAY_LABELS[i],
          date: fmtDate(d),
          monthLabel: fmtMonthName(d),
          key: DAY_KEYS[i],
          attendanceHrs: attEntry ? attEntry.hours : 0.0,
          hasAttendance: attEntry ? attEntry.hasRecord : false,
          activities: dayActivities,
          holiday: isHoliday,
          weekOff: isWeekOff,
          onLeave: isOnLeave,
          isDisabled: isDisabled,
        };
      });

      // Filter out Sunday if it is disabled (no work was recorded and no override)
      const filteredDays = computedDays.filter(day => {
        if (day.key === "sun") {
          return !day.isDisabled;
        }
        return true;
      });

      setWeekData(filteredDays);
    } catch (err) {
      console.error("Failed to load manager timesheet data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monday, employeeId]);

  useEffect(() => {
    if (userRole === "ADMIN") {
      const reviewRole = sessionStorage.getItem("team_review_role") || "MANAGER";
      if (reviewRole === "MANAGER") {
        const params = { week_start: fmtISO(monday), role: "MANAGER" };
        getTeamTimesheet(params)
          .then((res) => {
            const list = res.data.employees || [];
            const filtered = loggedUserId
              ? list.filter(emp => String(emp.employee_id) !== String(loggedUserId))
              : list;
            setReportingManagers(filtered);
          })
          .catch((err) => {
            console.error("Failed to load reporting managers:", err);
          });
      } else {
        const params = { week_start: fmtISO(monday), role: "EMPLOYEE" };
        if (resolvedManagerId) {
          params.manager_id = resolvedManagerId;
        } else if (employeeId) {
          return;
        }
        getTeamTimesheet(params)
          .then((res) => {
            const list = res.data.employees || [];
            const filtered = loggedUserId
              ? list.filter(emp => String(emp.employee_id) !== String(loggedUserId))
              : list;
            setReportingManagers(filtered);
          })
          .catch((err) => {
            console.error("Failed to load reporting managers:", err);
          });
      }
    } else if (userRole === "MANAGER" || userRole === "EMPLOYEE") {
      getTeamTimesheet({ week_start: fmtISO(monday) })
        .then((res) => {
          const list = res.data.employees || [];
          const filtered = loggedUserId
            ? list.filter(emp => String(emp.employee_id) !== String(loggedUserId))
            : list;
          setReportingManagers(filtered);
        })
        .catch((err) => {
          console.error("Failed to load reporting managers:", err);
        });
    }
  }, [userRole, monday, resolvedManagerId, employeeId, loggedUserId]);

  const selectedManagerName = useMemo(() => {
    const targetId = employeeId || loggedUserId;
    if (String(targetId) === String(loggedUserId)) {
      return useAuthStore.getState().fullName || "";
    }
    const m = reportingManagers.find(e => String(e.employee_id) === String(targetId));
    return m ? m.full_name : "";
  }, [reportingManagers, employeeId, loggedUserId]);

  useEffect(() => {
    if (userRole === "ADMIN" && !employeeId && reportingManagers.length > 0) {
      const targetEmp = reportingManagers[0];
      const mId = searchParams.get("manager_id") || resolvedManagerId;
      navigate(`/manager/activity?employee_id=${targetEmp.employee_id}${mId ? `&manager_id=${mId}` : ""}`, { replace: true });
    }
  }, [userRole, employeeId, reportingManagers, navigate, resolvedManagerId, searchParams]);

  useEffect(() => {
    if (weekData.length > 0 && !weekData.some(d => d.key === selectedKey)) {
      setSelectedKey("mon");
    }
  }, [weekData, selectedKey]);

  const selectedDay = weekData.find((d) => d.key === selectedKey);

  // ── Row field updater ────────────────────────────────────────────────────────
  const updateRow = (id, field, val) => {
    setWeekData((prev) =>
      prev.map((day) =>
        day.key === selectedKey
          ? { ...day, activities: day.activities.map((a) => a.id === id ? { ...a, [field]: val } : a) }
          : day
      )
    );
  };

  const findProject = (nameOrFormatted) => {
    if (!nameOrFormatted) return null;
    return dbProjects.find(p =>
      p.project_name === nameOrFormatted ||
      `${p.project_id} --- ${p.project_name}` === nameOrFormatted ||
      `${p.project_id} ${p.project_name}` === nameOrFormatted ||
      `[${p.project_id}] ${p.project_name}` === nameOrFormatted
    );
  };

  const getDisciplineOptionsForRow = (activity) => {
    if (!activity.project) return [];
    const proj = findProject(activity.project);
    if (!proj) return [];
    const disciplinesList = projectDisciplines[proj.project_id] || [];
    const dbDisciplines = disciplinesList.map(s => s.discipline_id);
    if (activity.discipline && !dbDisciplines.includes(activity.discipline)) {
      dbDisciplines.push(activity.discipline);
    }
    return dbDisciplines;
  };

  const getStageOptionsForRow = (activity) => {
    if (!activity.project || !activity.discipline) return [];
    const proj = findProject(activity.project);
    if (!proj) return [];
    const disciplinesList = projectDisciplines[proj.project_id] || [];
    const disciplineObj = disciplinesList.find(s => s.discipline_id.toUpperCase() === activity.discipline.toUpperCase());
    if (!disciplineObj) return [];
    const stagesList = disciplineStages[`${proj.project_id}_${disciplineObj.discipline_id}`] || [];
    const dbTypes = stagesList.map(d => d.stage_name).filter(Boolean);
    if (activity.stageName && !dbTypes.includes(activity.stageName)) {
      dbTypes.push(activity.stageName);
    }
    return dbTypes;
  };

  const handleProjectChange = (id, newProjectName) => {
    updateRow(id, "project", newProjectName);
    updateRow(id, "discipline", "");
    updateRow(id, "stageName", "");

    const proj = findProject(newProjectName);
    if (proj) {
      fetchDisciplinesForProject(proj.project_id);
    }
  };

  const handleDisciplineChange = (id, rowProjectName, newDisciplineId) => {
    updateRow(id, "discipline", newDisciplineId);
    updateRow(id, "stageName", "");

    const proj = findProject(rowProjectName);
    if (proj && newDisciplineId) {
      fetchStagesForDiscipline(proj.project_id, newDisciplineId);
    }
  };

  // ── Add new blank row ────────────────────────────────────────────────────────
  const addRow = () => {
    const blank = makeBlankRow();
    setWeekData((prev) =>
      prev.map((day) =>
        day.key === selectedKey
          ? { ...day, activities: [...day.activities, blank] }
          : day
      )
    );
  };

  // ── Delete row ───────────────────────────────────────────────────────────────
  const deleteRow = async (id) => {
    const act = selectedDay?.activities.find((a) => a.id === id);
    const isNew = act ? act.isNew : true;

    if (!isNew) {
      try {
        await deleteManagerActivity(id, employeeId ? { employee_id: employeeId } : {});
      } catch (err) {
        console.error("Failed to delete record", err);
        setErrorMsg("Failed to delete activity from database.");
        return;
      }
    }
    setWeekData((prev) =>
      prev.map((day) =>
        day.key === selectedKey
          ? { ...day, activities: day.activities.filter((a) => a.id !== id) }
          : day
      )
    );
  };

  // ── Summary ──────────────────────────────────────────────────────────────────
  const totalActualDecimal = useMemo(() =>
    (selectedDay?.activities || []).reduce((acc, a) => acc + hhmmToDecimal(a.duration), 0),
    [selectedDay]
  );
  const attendanceDecimal = selectedDay?.attendanceHrs ?? 0;
  const hasAttendanceData = selectedDay?.hasAttendance ?? false;

  // Productivity = (activity hrs / attendance hrs) × 100, capped at 100
  // Only meaningful when attendance hrs > 0
  const productivityPct = attendanceDecimal > 0
    ? Math.min((totalActualDecimal / attendanceDecimal) * 100, 100)
    : 0;
  const productivityColor =
    !hasAttendanceData ? "text-white/40"
      : attendanceDecimal === 0 ? "text-white/40"
        : productivityPct >= 80 ? "text-emerald-300"
          : productivityPct >= 50 ? "text-amber-300"
            : "text-red-400";
  const productivityBarColor =
    !hasAttendanceData || attendanceDecimal === 0 ? "bg-white/20"
      : productivityPct >= 80 ? "bg-emerald-400"
        : productivityPct >= 50 ? "bg-amber-400"
          : "bg-red-400";
  const productivityLabel =
    !hasAttendanceData ? "No Record"
      : attendanceDecimal === 0 ? "Not Checked Out"
        : productivityPct >= 80 ? "Excellent"
          : productivityPct >= 50 ? "Good"
            : "Low";


  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSaving(true); setSuccessMsg(""); setErrorMsg("");
    try {
      const dayIdx = DAY_KEYS.indexOf(selectedKey);
      const targetDateStr = fmtISO(addDays(monday, dayIdx));

      for (const a of (selectedDay?.activities || [])) {
        if (a.duration && a.project) {
          const payload = {
            activity_name: a.project + (a.stageName ? ` — ${a.stageName}` : ""),
            activity_date: targetDateStr,
            description: a.description,
            duration_hours: parseFloat(a.duration),
            project_name: a.project,
            discipline_name: a.discipline,
            stage_name: a.stageName,
          };

          if (a.isNew) {
            await createManagerActivity(payload);
          } else {
            await updateManagerActivity(a.id, payload, employeeId ? { employee_id: employeeId } : {});
          }
        }
      }
      setSuccessMsg("✅ Activities saved successfully!");
      await loadData();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (err) {
      setErrorMsg(err?.response?.data?.error || "Failed to save activities.");
    } finally { setSaving(false); }
  };

  const weekLabel = `${fmtDate(weekDates[0])} ${fmtMonthName(weekDates[0])} – ${fmtDate(weekDates[6])} ${fmtMonthName(weekDates[6])} ${weekDates[0].getFullYear()}`;

  // ── Shared cell style ────────────────────────────────────────────────────────
  const cell = "py-2.5 px-2 align-middle";

  const projectNames = dbProjects.map(p => `${p.project_id} --- ${p.project_name}`);

  return (
    <Layout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1300px] mx-auto">

          {/* HEADER */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-blue-500 flex items-center justify-center shadow-lg flex-shrink-0">
                <span className="text-2xl sm:text-3xl">📋</span>
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-white">
                  {selectedManagerName ? `${selectedManagerName} Weekly Time Sheet` : "Weekly Time Sheet"}
                </h1>
                <p className="text-white/60 text-xs sm:text-sm">
                  {userRole === "ADMIN" ? "" : "My "}Timesheet — Week of {weekLabel}
                </p>
              </div>
            </div>

            {/* Week Navigator */}
            <div className="flex items-center gap-2 self-start sm:self-auto">
              {!isOwnTimesheet && (userRole === "ADMIN" || reportingManagers.length > 0) ? (
                <select
                  value={employeeId || loggedUserId}
                  onChange={(e) => {
                    const val = e.target.value;
                    const emp = reportingManagers.find(x => String(x.employee_id) === String(val));
                    const mId = searchParams.get("manager_id") || resolvedManagerId;
                    if (emp && emp.role === "EMPLOYEE") {
                      navigate(`/employee/assigned-projects?employee_id=${val}${mId ? `&manager_id=${mId}` : ""}`);
                    } else {
                      navigate(`/manager/activity?employee_id=${val}${mId ? `&manager_id=${mId}` : ""}`);
                    }
                  }}
                  style={{ colorScheme: "dark" }}
                  className="bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl px-3 py-1.5 text-white text-xs sm:text-sm font-semibold transition-all focus:outline-none focus:border-indigo-400 cursor-pointer shadow"
                >
                  {reportingManagers.map((emp) => (
                    <option key={emp.employee_id} value={emp.employee_id} className="bg-gray-900 text-white">
                      {emp.full_name}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  onClick={() => navigate("/manager/activity/progress")}
                  className="flex-shrink-0 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs sm:text-sm font-semibold transition-all shadow whitespace-nowrap"
                >
                  📊 View Project Progress
                </button>
              )}
            </div>
          </div>

          {/* DAY STRIP */}
          <div className={`grid gap-2 sm:gap-3 mb-5 ${weekData.length === 6
            ? "grid-cols-3 sm:grid-cols-6"
            : "grid-cols-4 sm:grid-cols-7"
            }`}>
            {weekData.map((day) => {
              const isSelected = selectedKey === day.key;
              return (
                <button key={day.key} onClick={() => setSelectedKey(day.key)}
                  className={`rounded-2xl border text-center px-2 py-3 sm:py-4 transition-all cursor-pointer
                    ${isSelected ? "bg-yellow-300/20 border-yellow-400/50 shadow-lg scale-[1.04]" : "bg-white/10 border-white/20 hover:bg-white/15 hover:border-white/30"}`}>
                  <p className={`text-xs sm:text-sm font-bold leading-none mb-0.5 ${isSelected ? "text-white" : "text-white/70"}`}>{day.label}</p>
                  <p className={`text-xl sm:text-2xl font-extrabold leading-tight ${isSelected ? "text-white" : "text-white/50"}`}>{day.date}</p>
                  {day.holiday && <p className="text-amber-300/80 text-[9px] mt-0.5 font-semibold">Holiday</p>}
                  {day.weekOff && !day.holiday && <p className="text-white/40 text-[9px] mt-0.5 font-semibold">Week Off</p>}
                  {day.onLeave && <p className="text-sky-300/80 text-[9px] mt-0.5 font-semibold">On Leave</p>}
                  {isSelected && <div className="mt-1 w-1.5 h-1.5 rounded-full bg-yellow-400 mx-auto" />}
                </button>
              );
            })}
          </div>

          {/* MAIN CARD */}
          <div className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-4 sm:p-6">
            {loading ? (
              <div className="py-12 text-center">
                <p className="text-white/60 text-sm">Loading activities...</p>
              </div>
            ) : selectedDay?.isDisabled ? (
              <div className="py-12 text-center text-white">
                <p className="text-4xl mb-3">
                  {selectedDay.onLeave ? "🏝️" : selectedDay.holiday ? "🎉" : "🛋️"}
                </p>
                <p className="text-white/70 font-semibold text-lg">
                  {selectedDay.onLeave
                    ? "You were on Leave today"
                    : selectedDay.holiday
                      ? "Today is a Holiday"
                      : "Today is a Week Off"}
                </p>
                <p className="text-white/40 text-sm mt-1">Timesheet logging is disabled since no work was recorded.</p>
              </div>
            ) : (
              <>
                {/* ── Desktop Table ── */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="bg-white/10 border-b border-white/20 text-white/80 font-bold text-xs uppercase tracking-wider">
                        <th className="py-3 px-2 text-left w-[72px]">Date</th>
                        <th className="py-3 px-2 text-left w-[140px]">Project</th>
                        <th className="py-3 px-2 text-left w-[110px]">Discipline</th>
                        <th className="py-3 px-2 text-left w-[160px]">Stage</th>
                        <th className="py-3 px-2 text-left">Description</th>
                        <th className="py-3 px-2 text-center w-[90px]">Duration</th>
                        <th className="py-3 px-2 text-center w-[80px]">Status</th>
                        <th className="py-3 px-2 text-center w-[40px]"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedDay?.activities || []).length === 0 && (
                        <tr><td colSpan={8} className="py-10 text-center text-white/50 italic text-sm">No activities yet — click ＋ Add Activity below</td></tr>
                      )}
                      {(selectedDay?.activities || []).map((activity) => {
                        const isSaved = !activity.isNew && activity.duration !== "";
                        return (
                          <tr key={activity.id} className="border-b border-white/10 hover:bg-white/5 transition-colors">

                            {/* Date */}
                            <td className={cell}>
                              <span className="text-white/55 text-xs">{activity.date || `${selectedDay.date}-${selectedDay.monthLabel}`}</span>
                            </td>

                            {/* Project dropdown */}
                            <td className={cell}>
                              <InlineSelect
                                value={activity.project}
                                onChange={(v) => handleProjectChange(activity.id, v)}
                                options={projectNames}
                                placeholder="Project…"
                                disabled={!canEditExisting}
                              />
                            </td>

                            {/* Discipline dropdown */}
                            <td className={cell}>
                              <InlineSelect
                                value={activity.discipline}
                                onChange={(v) => handleDisciplineChange(activity.id, activity.project, v)}
                                options={getDisciplineOptionsForRow(activity)}
                                placeholder="Discipline…"
                                colorMap={DISCIPLINE_COLORS}
                                disabled={!canEditExisting}
                              />
                            </td>

                            {/* Stage dropdown */}
                            <td className={cell}>
                              <InlineSelect
                                value={activity.stageName}
                                onChange={(v) => updateRow(activity.id, "stageName", v)}
                                options={getStageOptionsForRow(activity)}
                                placeholder="Stage…"
                                disabled={!canEditExisting}
                              />
                            </td>

                            {/* Description */}
                            <td className={cell}>
                              <input
                                type="text"
                                value={activity.description}
                                onChange={(e) => updateRow(activity.id, "description", e.target.value)}
                                placeholder="Add note…"
                                disabled={!canEditExisting}
                                className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-400 focus:bg-slate-800 placeholder-white/25 transition-all disabled:opacity-75 disabled:cursor-not-allowed"
                              />
                            </td>

                            {/* Duration */}
                            <td className={`${cell} text-center`}>
                              <TimeInput
                                value={activity.duration}
                                onChange={(v) => updateRow(activity.id, "duration", v)}
                                disabled={!canEditExisting}
                              />
                            </td>

                            {/* Status */}
                            <td className={`${cell} text-center`}>
                              {isSaved ? (
                                <span className="inline-block px-2 py-1 rounded-lg bg-emerald-500/25 text-emerald-300 text-xs font-semibold">✔ Saved</span>
                              ) : activity.duration !== "" ? (
                                <span className="inline-block px-2 py-1 rounded-lg bg-amber-500/20 text-amber-300 text-xs font-semibold">● Draft</span>
                              ) : (
                                <span className="inline-block px-2 py-1 rounded-lg bg-white/10 text-white/35 text-xs">— Empty</span>
                              )}
                            </td>

                            {/* Delete */}
                            <td className={`${cell} text-center`}>
                              {isOwnTimesheet && (
                                <button onClick={() => deleteRow(activity.id)}
                                  className="w-6 h-6 rounded-lg bg-red-500/15 hover:bg-red-500/30 text-red-400 text-xs flex items-center justify-center transition-all mx-auto">
                                  ✕
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ── Mobile Cards ── */}
                <div className="sm:hidden space-y-3">
                  {(selectedDay?.activities || []).length === 0 && (
                    <p className="text-center italic text-white/50 py-6 text-sm">No activities yet</p>
                  )}
                  {(selectedDay?.activities || []).map((activity) => {
                    const isSaved = !activity.isNew && activity.duration !== "";
                    return (
                      <div key={activity.id} className="bg-white/10 rounded-xl p-4 border border-white/20 space-y-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-white/55 text-xs">{activity.date || `${selectedDay.date}-${selectedDay.monthLabel}`}</span>
                          <div className="flex items-center gap-2">
                            {isSaved ? (
                              <span className="px-2 py-0.5 rounded-lg bg-emerald-500/25 text-emerald-300 text-xs font-semibold">✔ Saved</span>
                            ) : activity.duration !== "" ? (
                              <span className="px-2 py-0.5 rounded-lg bg-amber-500/20 text-amber-300 text-xs font-semibold">● Draft</span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-lg bg-white/10 text-white/35 text-xs">— Empty</span>
                            )}
                            {isOwnTimesheet && (
                              <button onClick={() => deleteRow(activity.id)}
                                className="w-6 h-6 rounded-lg bg-red-500/15 hover:bg-red-500/30 text-red-400 text-xs flex items-center justify-center">✕</button>
                            )}
                          </div>
                        </div>
                        <InlineSelect value={activity.project} onChange={(v) => handleProjectChange(activity.id, v)} options={projectNames} placeholder="Project…" disabled={!canEditExisting} />
                        <div className="grid grid-cols-2 gap-2">
                          <InlineSelect value={activity.discipline} onChange={(v) => handleDisciplineChange(activity.id, activity.project, v)} options={getDisciplineOptionsForRow(activity)} placeholder="Discipline…" colorMap={DISCIPLINE_COLORS} disabled={!canEditExisting} />
                          <InlineSelect value={activity.stageName} onChange={(v) => updateRow(activity.id, "stageName", v)} options={getStageOptionsForRow(activity)} placeholder="Stage…" disabled={!canEditExisting} />
                        </div>
                        <input type="text" value={activity.description} onChange={(e) => updateRow(activity.id, "description", e.target.value)}
                          placeholder="Add note…"
                          disabled={!canEditExisting}
                          className="w-full bg-white/10 border border-white/20 rounded-lg px-2 py-1.5 text-white text-xs focus:outline-none focus:border-indigo-400 focus:bg-slate-800 placeholder-white/25 disabled:opacity-75 disabled:cursor-not-allowed" />
                        <div className="flex items-center justify-between">
                          <span className="text-white/50 text-xs">Duration (HH:MM)</span>
                          <TimeInput value={activity.duration} onChange={(v) => updateRow(activity.id, "duration", v)} disabled={!canEditExisting} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* ── SUMMARY ── */}
                <div className="mt-5 pt-4 border-t border-white/15 grid grid-cols-2 sm:grid-cols-3 gap-3">

                  {/* Total Activity Hrs */}
                  <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center">
                    <p className="text-white/50 text-[10px] uppercase tracking-wider mb-1">Total Activity Hrs</p>
                    <p className="text-white text-xl font-bold font-mono">{toHHMM(totalActualDecimal)}</p>
                    <p className="text-white/30 text-[10px] mt-0.5">logged today</p>
                  </div>

                  {/* Total Attendance Hrs */}
                  <div className="bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-center">
                    <p className="text-white/50 text-[10px] uppercase tracking-wider mb-1">Total Attendance Hrs</p>
                    <p className={`text-xl font-bold font-mono ${!hasAttendanceData ? "text-white/30"
                      : attendanceDecimal > 0 ? "text-white"
                        : "text-amber-300"
                      }`}>
                      {!hasAttendanceData
                        ? "--:--"
                        : toHHMM(attendanceDecimal)}
                    </p>
                    <p className="text-white/30 text-[10px] mt-0.5">
                      {!hasAttendanceData
                        ? "no record found"
                        : attendanceDecimal > 0
                          ? "from biometric"
                          : "checked in, no checkout"}
                    </p>
                  </div>

                  {/* Productivity */}
                  <div className="col-span-2 sm:col-span-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3">
                    <p className="text-white/50 text-[10px] uppercase tracking-wider mb-2 text-center">Productivity</p>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className={`text-2xl font-bold font-mono ${productivityColor}`}>
                        {hasAttendanceData && attendanceDecimal > 0
                          ? `${Math.round(productivityPct)}%`
                          : "—"}
                      </span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${!hasAttendanceData || attendanceDecimal === 0
                        ? "bg-white/10 text-white/40"
                        : productivityPct >= 80 ? "bg-emerald-500/25 text-emerald-300"
                          : productivityPct >= 50 ? "bg-amber-500/25 text-amber-300"
                            : "bg-red-500/25 text-red-400"
                        }`}>
                        {productivityLabel}
                      </span>
                    </div>
                    {/* Progress bar */}
                    <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${productivityBarColor}`}
                        style={{ width: `${productivityPct}%` }}
                      />
                    </div>
                    <p className="text-white/30 text-[10px] mt-1 text-center">
                      {hasAttendanceData && attendanceDecimal > 0
                        ? `${toHHMM(totalActualDecimal)} activity ÷ ${toHHMM(attendanceDecimal)} attendance`
                        : hasAttendanceData
                          ? "Check-out to calculate productivity"
                          : "No attendance record for this day"}
                    </p>
                  </div>

                </div>


                {successMsg && (
                  <div className="mt-3 px-4 py-3 rounded-xl bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 text-sm font-semibold">{successMsg}</div>
                )}
                {errorMsg && (
                  <div className="mt-3 px-4 py-3 rounded-xl bg-red-500/15 border border-red-500/25 text-red-300 text-sm font-semibold">{errorMsg}</div>
                )}

                {/* Column Guide */}
                <div className="mt-4 pt-3 border-t border-white/10">
                  <p className="text-white/35 text-xs leading-relaxed">
                    <span className="text-white/50 font-semibold">Column Guide: </span>
                    Duration = type as HH:MM (e.g. 02:30) &nbsp;·&nbsp;
                    All dropdowns are editable per row &nbsp;·&nbsp;
                    Attendance Hrs = auto from biometric
                  </p>
                </div>

                {(userRole === "ADMIN" || (userRole === "MANAGER" && employeeId)) && (
                  <div className="mt-4 flex justify-start">
                    <button
                      onClick={() => navigate(userRole === "ADMIN" ? "/admin/team-review" : "/manager/team-review")}
                      className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs sm:text-sm font-semibold transition-all shadow flex items-center gap-2"
                    >
                      ← Back to Team Review
                    </button>
                  </div>
                )}

                {/* Actions */}
                {canEditExisting && (
                  <div className="flex items-center justify-between mt-4 gap-3">
                    {isOwnTimesheet ? (
                      <button onClick={addRow}
                        className="px-4 py-2 rounded-xl border border-dashed border-white/30 bg-white/8 hover:bg-white/15 text-white text-xs font-semibold transition-all flex items-center gap-1.5">
                        ＋ Add Activity
                      </button>
                    ) : <div />}
                    <button onClick={handleSubmit} disabled={saving}
                      className="px-5 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 hover:from-indigo-600 hover:to-blue-600 text-white text-xs font-semibold shadow-lg transition-all hover:scale-105 disabled:opacity-60 disabled:hover:scale-100">
                      {saving ? "Saving..." : "Save Entrie"}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
